import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MetaPixel } from '../services/metaPixel';
import { toSarvamTargetLanguageCode } from '@/utils/sarvamLocale';
import neproLogo from '@/assets/images/nepro-logo.png';

const PLAY_STORE_URL = 'https://nepro.onelink.me/IlCs'; // AppsFlyer OneLink — replaces raw Play Store URL for tracked attribution

// QR code API — encodes the full Play Store URL with deep link params
function getQrCodeUrl(deepLinkUrl: string, pixelSize = 200): string {
  const s = Math.min(280, Math.max(96, Math.round(pixelSize)));
  return `https://api.qrserver.com/v1/create-qr-code/?size=${s}x${s}&data=${encodeURIComponent(deepLinkUrl)}&color=7210FF&bgcolor=ffffff&margin=8`;
}

interface LangStrings {
  openingApp: string;
  pleaseWait: string;
  tagline: string;
  headline: string;
  description: string;
  benefits: string[];
  downloadCta: string;
  availableOn: string;
  scanQr: string;
  orScanQr: string;
  desktopNote: string;
}

// Fully translated strings for all 11 supported languages
const TRANSLATIONS: Record<string, LangStrings> = {
  en: {
    openingApp: 'Opening NePro App...',
    pleaseWait: 'Please wait while we redirect you',
    tagline: "Join India's Growing Service Marketplace",
    headline: 'Start Your Service Business 🚀',
    description: 'Register as a service provider on NePro. Get discovered by customers, manage bookings, and grow your business — all for free.',
    benefits: [
      '✅ Free registration — no fees ever',
      '✅ Your own digital business profile',
      '✅ Direct customer inquiries',
      '✅ Build reputation with reviews',
      '✅ Track business growth',
    ],
    downloadCta: 'Download NePro App →',
    availableOn: 'Available on Google Play Store',
    scanQr: 'Scan QR to Download',
    orScanQr: 'Or scan this QR code with your phone',
    desktopNote: 'Open this page on your Android phone to download directly.',
  },
  hi: {
    openingApp: 'NePro App खोल रहे हैं...',
    pleaseWait: 'कृपया प्रतीक्षा करें',
    tagline: 'भारत का बढ़ता हुआ सेवा बाज़ार',
    headline: 'अपनी सेवा व्यापार शुरू करें 🚀',
    description: 'NePro पर सेवा प्रदाता के रूप में पंजीकरण करें। ग्राहकों द्वारा खोजे जाएं, बुकिंग प्रबंधित करें, और अपने व्यापार को बढ़ाएं — सब कुछ मुफ्त में।',
    benefits: [
      '✅ मुफ्त पंजीकरण — कोई शुल्क नहीं',
      '✅ अपनी डिजिटल व्यापार प्रोफ़ाइल',
      '✅ सीधे ग्राहक पूछताछ',
      '✅ समीक्षाओं से प्रतिष्ठा बनाएं',
      '✅ व्यापार विकास को ट्रैक करें',
    ],
    downloadCta: 'NePro App डाउनलोड करें →',
    availableOn: 'Google Play Store पर उपलब्ध',
    scanQr: 'डाउनलोड के लिए QR स्कैन करें',
    orScanQr: 'या अपने फोन से यह QR कोड स्कैन करें',
    desktopNote: 'सीधे डाउनलोड करने के लिए इस पेज को अपने Android फोन पर खोलें।',
  },
  kn: {
    openingApp: 'NePro App ತೆರೆಯುತ್ತಿದೆ...',
    pleaseWait: 'ದಯವಿಟ್ಟು ನಿರೀಕ್ಷಿಸಿ',
    tagline: 'ಭಾರತದ ಬೆಳೆಯುತ್ತಿರುವ ಸೇವಾ ಮಾರುಕಟ್ಟೆ',
    headline: 'ನಿಮ್ಮ ಸೇವಾ ವ್ಯವಹಾರ ಪ್ರಾರಂಭಿಸಿ 🚀',
    description: 'NePro ನಲ್ಲಿ ಸೇವಾ ಪೂರೈಕೆದಾರರಾಗಿ ನೋಂದಾಯಿಸಿ. ಗ್ರಾಹಕರಿಂದ ಕಂಡುಹಿಡಿಯಲ್ಪಡಿ, ಬುಕಿಂಗ್‌ಗಳನ್ನು ನಿರ್ವಹಿಸಿ ಮತ್ತು ನಿಮ್ಮ ವ್ಯವಹಾರವನ್ನು ಬೆಳೆಸಿ — ಎಲ್ಲವೂ ಉಚಿತ.',
    benefits: [
      '✅ ಉಚಿತ ನೋಂದಣಿ — ಯಾವುದೇ ಶುಲ್ಕವಿಲ್ಲ',
      '✅ ನಿಮ್ಮ ಸ್ವಂತ ಡಿಜಿಟಲ್ ವ್ಯವಹಾರ ಪ್ರೊಫೈಲ್',
      '✅ ನೇರ ಗ್ರಾಹಕ ವಿಚಾರಣೆಗಳು',
      '✅ ವಿಮರ್ಶೆಗಳೊಂದಿಗೆ ಖ್ಯಾತಿ ನಿರ್ಮಿಸಿ',
      '✅ ವ್ಯವಹಾರ ಬೆಳವಣಿಗೆಯನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ',
    ],
    downloadCta: 'NePro App ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ →',
    availableOn: 'Google Play Store ನಲ್ಲಿ ಲಭ್ಯ',
    scanQr: 'ಡೌನ್‌ಲೋಡ್‌ಗಾಗಿ QR ಸ್ಕ್ಯಾನ್ ಮಾಡಿ',
    orScanQr: 'ಅಥವಾ ನಿಮ್ಮ ಫೋನ್‌ನಿಂದ ಈ QR ಕೋಡ್ ಸ್ಕ್ಯಾನ್ ಮಾಡಿ',
    desktopNote: 'ನೇರವಾಗಿ ಡೌನ್‌ಲೋಡ್ ಮಾಡಲು ನಿಮ್ಮ Android ಫೋನ್‌ನಲ್ಲಿ ಈ ಪೇಜ್ ತೆರೆಯಿರಿ.',
  },
  ml: {
    openingApp: 'NePro App തുറക്കുന്നു...',
    pleaseWait: 'ദയവായി കാത്തിരിക്കൂ',
    tagline: 'ഇന്ത്യയുടെ വളരുന്ന സേവന മാർക്കറ്റ്',
    headline: 'നിങ്ങളുടെ സേവന ബിസിനസ്സ് ആരംഭിക്കൂ 🚀',
    description: 'NePro-ൽ ഒരു സേവന ദാതാവായി രജിസ്റ്റർ ചെയ്യൂ. ഉപഭോക്താക്കൾ നിങ്ങളെ കണ്ടെത്തട്ടെ, ബുക്കിംഗുകൾ നിയന്ത്രിക്കൂ, ബിസിനസ്സ് വളർത്തൂ — എല്ലാം സൗജന്യമായി.',
    benefits: [
      '✅ സൗജന്യ രജിസ്ട്രേഷൻ — ഫീസ് ഇല്ല',
      '✅ നിങ്ങളുടെ സ്വന്തം ഡിജിറ്റൽ ബിസിനസ്സ് പ്രൊഫൈൽ',
      '✅ നേരിട്ടുള്ള ഉപഭോക്തൃ അന്വേഷണങ്ങൾ',
      '✅ റിവ്യൂകൾ ഉപയോഗിച്ച് പ്രശസ്തി നിർമ്മിക്കൂ',
      '✅ ബിസിനസ്സ് വളർച്ച ട്രാക്ക് ചെയ്യൂ',
    ],
    downloadCta: 'NePro App ഡൗൺലോഡ് ചെയ്യൂ →',
    availableOn: 'Google Play Store-ൽ ലഭ്യം',
    scanQr: 'ഡൗൺലോഡ് ചെയ്യാൻ QR സ്കാൻ ചെയ്യൂ',
    orScanQr: 'അല്ലെങ്കിൽ നിങ്ങളുടെ ഫോണിൽ നിന്ന് ഈ QR കോഡ് സ്കാൻ ചെയ്യൂ',
    desktopNote: 'നേരിട്ട് ഡൗൺലോഡ് ചെയ്യാൻ നിങ്ങളുടെ Android ഫോണിൽ ഈ പേജ് തുറക്കൂ.',
  },
  ta: {
    openingApp: 'NePro App திறக்கிறது...',
    pleaseWait: 'தயவுசெய்து காத்திருங்கள்',
    tagline: 'இந்தியாவின் வளரும் சேவை சந்தை',
    headline: 'உங்கள் சேவை வணிகத்தை தொடங்குங்கள் 🚀',
    description: 'NePro-வில் சேவை வழங்குநராக பதிவு செய்யுங்கள். வாடிக்கையாளர்களால் கண்டுபிடிக்கப்படுங்கள், முன்பதிவுகளை நிர்வகியுங்கள், வணிகத்தை வளர்த்துக்கொள்ளுங்கள் — அனைத்தும் இலவசம்.',
    benefits: [
      '✅ இலவச பதிவு — கட்டணம் இல்லை',
      '✅ உங்கள் சொந்த டிஜிட்டல் வணிக சுயவிவரம்',
      '✅ நேரடி வாடிக்கையாளர் விசாரணைகள்',
      '✅ மதிப்புரைகள் மூலம் நற்பெயர் உருவாக்குங்கள்',
      '✅ வணிக வளர்ச்சியை கண்காணியுங்கள்',
    ],
    downloadCta: 'NePro App பதிவிறக்கம் →',
    availableOn: 'Google Play Store-ல் கிடைக்கிறது',
    scanQr: 'பதிவிறக்க QR ஸ்கேன் செய்யுங்கள்',
    orScanQr: 'அல்லது உங்கள் போனில் இந்த QR குறியீட்டை ஸ்கேன் செய்யுங்கள்',
    desktopNote: 'நேரடியாக பதிவிறக்க உங்கள் Android போனில் இந்த பக்கத்தை திறக்கவும்.',
  },
  te: {
    openingApp: 'NePro App తెరుచుకుంటోంది...',
    pleaseWait: 'దయచేసి వేచి ఉండండి',
    tagline: 'భారతదేశపు పెరుగుతున్న సేవా మార్కెట్',
    headline: 'మీ సేవా వ్యాపారాన్ని ప్రారంభించండి 🚀',
    description: 'NePro లో సేవా సరఫరాదారుగా నమోదు చేసుకోండి. వినియోగదారులు మిమ్మల్ని కనుగొనేలా చేయండి, బుకింగ్‌లు నిర్వహించండి, వ్యాపారాన్ని పెంచుకోండి — అన్నీ ఉచితంగా.',
    benefits: [
      '✅ ఉచిత నమోదు — ఎటువంటి రుసుము లేదు',
      '✅ మీ స్వంత డిజిటల్ వ్యాపార ప్రొఫైల్',
      '✅ నేరుగా వినియోగదారు విచారణలు',
      '✅ సమీక్షలతో పేరు నిర్మించుకోండి',
      '✅ వ్యాపార వృద్ధిని ట్రాక్ చేయండి',
    ],
    downloadCta: 'NePro App డౌన్‌లోడ్ చేయండి →',
    availableOn: 'Google Play Store లో అందుబాటులో ఉంది',
    scanQr: 'డౌన్‌లోడ్ కోసం QR స్కాన్ చేయండి',
    orScanQr: 'లేదా మీ ఫోన్‌తో ఈ QR కోడ్ స్కాన్ చేయండి',
    desktopNote: 'నేరుగా డౌన్‌లోడ్ చేయడానికి మీ Android ఫోన్‌లో ఈ పేజీని తెరవండి.',
  },
  mr: {
    openingApp: 'NePro App उघडत आहे...',
    pleaseWait: 'कृपया प्रतीक्षा करा',
    tagline: 'भारताची वाढती सेवा बाजारपेठ',
    headline: 'तुमचा सेवा व्यवसाय सुरू करा 🚀',
    description: 'NePro वर सेवा पुरवठादार म्हणून नोंदणी करा. ग्राहकांना तुम्ही सापडा, बुकिंग व्यवस्थापित करा आणि व्यवसाय वाढवा — सर्व काही मोफत.',
    benefits: [
      '✅ मोफत नोंदणी — कोणतेही शुल्क नाही',
      '✅ तुमची स्वतःची डिजिटल व्यवसाय प्रोफाइल',
      '✅ थेट ग्राहक चौकशी',
      '✅ पुनरावलोकनांसह प्रतिष्ठा निर्माण करा',
      '✅ व्यवसाय वाढ ट्रॅक करा',
    ],
    downloadCta: 'NePro App डाउनलोड करा →',
    availableOn: 'Google Play Store वर उपलब्ध',
    scanQr: 'डाउनलोडसाठी QR स्कॅन करा',
    orScanQr: 'किंवा तुमच्या फोनने हा QR कोड स्कॅन करा',
    desktopNote: 'थेट डाउनलोड करण्यासाठी तुमच्या Android फोनवर हे पेज उघडा.',
  },
  gu: {
    openingApp: 'NePro App ખોલી રહ્યા છીએ...',
    pleaseWait: 'કૃપા કરીને રાહ જુઓ',
    tagline: 'ભારતની વધતી સેવા બજાર',
    headline: 'તમારો સેવા વ્યવસાય શરૂ કરો 🚀',
    description: 'NePro પર સેવા પ્રદાતા તરીકે નોંધણી કરો. ગ્રાહકો દ્વારા શોધાઓ, બુકિંગ સંચાલિત કરો, અને વ્યવસાય વધારો — બધું મફત.',
    benefits: [
      '✅ મફત નોંધણી — કોઈ ફી નહીં',
      '✅ તમારી પોતાની ડિજિટલ વ્યવસાય પ્રોફાઇલ',
      '✅ સીધી ગ્રાહક પૂછપરછ',
      '✅ સમીક્ષાઓ સાથે પ્રતિષ્ઠા બનાવો',
      '✅ વ્યવસાય વૃદ્ધિ ટ્રૅક કરો',
    ],
    downloadCta: 'NePro App ડાઉનલોડ કરો →',
    availableOn: 'Google Play Store પર ઉપલબ્ધ',
    scanQr: 'ડાઉનલોડ માટે QR સ્કૅન કરો',
    orScanQr: 'અથવા તમારા ફોનથી આ QR કોડ સ્કૅન કરો',
    desktopNote: 'સીધું ડાઉનલોડ કરવા માટે તમારા Android ફોન પર આ પેજ ખોલો.',
  },
  pa: {
    openingApp: 'NePro App ਖੋਲ੍ਹਿਆ ਜਾ ਰਿਹਾ ਹੈ...',
    pleaseWait: 'ਕਿਰਪਾ ਕਰਕੇ ਉਡੀਕ ਕਰੋ',
    tagline: 'ਭਾਰਤ ਦਾ ਵਧਦਾ ਸੇਵਾ ਬਾਜ਼ਾਰ',
    headline: 'ਆਪਣਾ ਸੇਵਾ ਕਾਰੋਬਾਰ ਸ਼ੁਰੂ ਕਰੋ 🚀',
    description: 'NePro ਤੇ ਸੇਵਾ ਪ੍ਰਦਾਤਾ ਵਜੋਂ ਰਜਿਸਟਰ ਕਰੋ। ਗਾਹਕਾਂ ਦੁਆਰਾ ਲੱਭੇ ਜਾਓ, ਬੁਕਿੰਗ ਪ੍ਰਬੰਧਿਤ ਕਰੋ, ਅਤੇ ਕਾਰੋਬਾਰ ਵਧਾਓ — ਸਭ ਮੁਫ਼ਤ।',
    benefits: [
      '✅ ਮੁਫ਼ਤ ਰਜਿਸਟ੍ਰੇਸ਼ਨ — ਕੋਈ ਫ਼ੀਸ ਨਹੀਂ',
      '✅ ਆਪਣੀ ਡਿਜੀਟਲ ਕਾਰੋਬਾਰ ਪ੍ਰੋਫਾਈਲ',
      '✅ ਸਿੱਧੀ ਗਾਹਕ ਪੁੱਛਗਿੱਛ',
      '✅ ਸਮੀਖਿਆਵਾਂ ਨਾਲ ਸਾਖ ਬਣਾਓ',
      '✅ ਕਾਰੋਬਾਰ ਵਿਕਾਸ ਟਰੈਕ ਕਰੋ',
    ],
    downloadCta: 'NePro App ਡਾਊਨਲੋਡ ਕਰੋ →',
    availableOn: 'Google Play Store ਤੇ ਉਪਲਬਧ',
    scanQr: 'ਡਾਊਨਲੋਡ ਲਈ QR ਸਕੈਨ ਕਰੋ',
    orScanQr: 'ਜਾਂ ਆਪਣੇ ਫ਼ੋਨ ਨਾਲ ਇਹ QR ਕੋਡ ਸਕੈਨ ਕਰੋ',
    desktopNote: 'ਸਿੱਧਾ ਡਾਊਨਲੋਡ ਕਰਨ ਲਈ ਆਪਣੇ Android ਫ਼ੋਨ ਤੇ ਇਹ ਪੇਜ ਖੋਲ੍ਹੋ।',
  },
  bn: {
    openingApp: 'NePro App খোলা হচ্ছে...',
    pleaseWait: 'অনুগ্রহ করে অপেক্ষা করুন',
    tagline: 'ভারতের ক্রমবর্ধমান সেবা বাজার',
    headline: 'আপনার সেবা ব্যবসা শুরু করুন 🚀',
    description: 'NePro-তে একজন সেবা প্রদানকারী হিসেবে নিবন্ধন করুন। গ্রাহকদের দ্বারা আবিষ্কৃত হন, বুকিং পরিচালনা করুন এবং ব্যবসা বাড়ান — সব বিনামূল্যে।',
    benefits: [
      '✅ বিনামূল্যে নিবন্ধন — কোনো ফি নেই',
      '✅ আপনার নিজস্ব ডিজিটাল ব্যবসায়িক প্রোফাইল',
      '✅ সরাসরি গ্রাহক অনুসন্ধান',
      '✅ রিভিউ দিয়ে সুনাম তৈরি করুন',
      '✅ ব্যবসার বৃদ্ধি ট্র্যাক করুন',
    ],
    downloadCta: 'NePro App ডাউনলোড করুন →',
    availableOn: 'Google Play Store-এ পাওয়া যাচ্ছে',
    scanQr: 'ডাউনলোডের জন্য QR স্ক্যান করুন',
    orScanQr: 'অথবা আপনার ফোন দিয়ে এই QR কোড স্ক্যান করুন',
    desktopNote: 'সরাসরি ডাউনলোড করতে আপনার Android ফোনে এই পেজটি খুলুন।',
  },
  or: {
    openingApp: 'NePro App ଖୋଲୁଛି...',
    pleaseWait: 'ଦୟାକରି ଅପେକ୍ଷା କରନ୍ତୁ',
    tagline: 'ଭାରତର ବଢ଼ୁଥିବା ସେବା ବଜାର',
    headline: 'ଆପଣଙ୍କ ସେବା ବ୍ୟବସାୟ ଆରମ୍ଭ କରନ୍ତୁ 🚀',
    description: 'NePro ରେ ଜଣେ ସେବା ପ୍ରଦାନକାରୀ ଭାବରେ ପଞ୍ଜୀକୃତ ହୁଅନ୍ତୁ। ଗ୍ରାହକଙ୍କ ଦ୍ୱାରା ଆବିଷ୍କୃତ ହୁଅନ୍ତୁ, ବୁକିଂ ପରିଚାଳନା କରନ୍ତୁ ଏବଂ ବ୍ୟବସାୟ ବଢ଼ାନ୍ତୁ — ସବୁ ବିନାମୂଲ୍ୟରେ।',
    benefits: [
      '✅ ବିନାମୂଲ୍ୟ ପଞ୍ଜୀକରଣ — କୌଣସି ଶୁଳ୍କ ନାହିଁ',
      '✅ ଆପଣଙ୍କ ନିଜସ୍ୱ ଡିଜିଟାଲ ବ୍ୟବସାୟ ପ୍ରୋଫାଇଲ',
      '✅ ସିଧା ଗ୍ରାହକ ଅନୁସନ୍ଧାନ',
      '✅ ସମୀକ୍ଷା ସହ ସୁନାମ ଗଢ଼ନ୍ତୁ',
      '✅ ବ୍ୟବସାୟ ବୃଦ୍ଧି ଟ୍ରାକ୍ କରନ୍ତୁ',
    ],
    downloadCta: 'NePro App ଡାଉନଲୋଡ କରନ୍ତୁ →',
    availableOn: 'Google Play Store ରେ ଉପଲବ୍ଧ',
    scanQr: 'ଡାଉନଲୋଡ ପାଇଁ QR ସ୍କ୍ୟାନ କରନ୍ତୁ',
    orScanQr: 'ଅଥବା ଆପଣଙ୍କ ଫୋନ ଦ୍ୱାରା ଏହି QR କୋଡ ସ୍କ୍ୟାନ କରନ୍ତୁ',
    desktopNote: 'ସିଧା ଡାଉନଲୋଡ ପାଇଁ ଆପଣଙ୍କ Android ଫୋନ ରେ ଏହି ପୃଷ୍ଠା ଖୋଲନ୍ତୁ।',
  },
};

const JoinRedirect = () => {
  const [searchParams] = useSearchParams();
  const [showFallback, setShowFallback] = useState(false);
  const [dynamicTranslation, setDynamicTranslation] = useState<LangStrings | null>(null);
  const [translating, setTranslating] = useState(false);

  // Get language from URL params, default to English
  // Accept both ?language= (from deep links) and ?lang= (from Meta ad URLs)
  const lang = searchParams.get('language') || searchParams.get('lang') || 'en';
  const baseT = TRANSLATIONS[lang] || TRANSLATIONS['en'];
  const t = dynamicTranslation || baseT;

  // Build the full Play Store URL with deep link params (used for QR code)
  const ref = searchParams.get('ref') || '';
  const id = searchParams.get('id') || '';
  const serviceId = searchParams.get('serviceId') || '';
  const cityId = searchParams.get('cityId') || '';
  const language = searchParams.get('language') || searchParams.get('lang') || '';
  const utmSource = searchParams.get('utm_source') || '';
  const utmContent = searchParams.get('utm_content') || '';

  const deepLinkParams = new URLSearchParams();
  if (ref) deepLinkParams.set('ref', ref);
  if (id) deepLinkParams.set('id', id);
  if (serviceId) deepLinkParams.set('serviceId', serviceId);
  if (cityId) deepLinkParams.set('cityId', cityId);
  if (language) deepLinkParams.set('language', language);
  if (utmSource) deepLinkParams.set('utm_source', utmSource);
  if (utmContent) deepLinkParams.set('utm_content', utmContent);

  const playStoreUrlWithParams = `${PLAY_STORE_URL}?${deepLinkParams.toString()}`;

  // Attempt Sarvam AI dynamic translation for languages not in TRANSLATIONS
  useEffect(() => {
    if (TRANSLATIONS[lang]) return; // Already have static translation
    const sarvamCode = toSarvamTargetLanguageCode(lang);
    if (!sarvamCode) return;
    const apiKey = import.meta.env.VITE_SARVAM_API_KEY;
    if (!apiKey) return;

    setTranslating(true);
    const en = TRANSLATIONS['en'];
    const textsToTranslate = [
      en.openingApp, en.pleaseWait, en.tagline, en.headline,
      en.description, ...en.benefits,
      en.downloadCta, en.availableOn, en.scanQr, en.orScanQr, en.desktopNote,
    ];

    Promise.all(
      textsToTranslate.map(text =>
        fetch('https://api.sarvam.ai/translate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'api-subscription-key': apiKey },
          body: JSON.stringify({ input: text, source_language_code: 'en-IN', target_language_code: sarvamCode, model: 'sarvam-translate:v1' }),
        }).then(r => r.json()).then(j => j.translated_text || text).catch(() => text)
      )
    ).then(results => {
      setDynamicTranslation({
        openingApp: results[0],
        pleaseWait: results[1],
        tagline: results[2],
        headline: results[3],
        description: results[4],
        benefits: results.slice(5, 10),
        downloadCta: results[10],
        availableOn: results[11],
        scanQr: results[12],
        orScanQr: results[13],
        desktopNote: results[14],
      });
    }).finally(() => setTranslating(false));
  }, [lang]);

  useEffect(() => {
    // Track lead event for Meta Pixel
    MetaPixel.lead({ leadId: id, source: ref });

    const deepLinkQuery = deepLinkParams.toString();
    const ua = navigator.userAgent.toLowerCase();
    const isMobile = /android|iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);

    if (isMobile) {
      if (isAndroid) {
        const intentUrl = `intent://join?${deepLinkQuery}#Intent;scheme=nepro;package=com.neprotech.nepro;S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`;
        window.location.href = intentUrl;
      } else {
        const deepLink = `nepro://join?${deepLinkQuery}`;
        window.location.href = deepLink;
        setTimeout(() => { window.location.href = PLAY_STORE_URL; }, 2500);
      }
      setTimeout(() => setShowFallback(true), 3000);
    } else {
      setShowFallback(true);
    }
  }, [searchParams]);

  if (!showFallback) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
        flexDirection: 'column', gap: '16px',
        background: 'linear-gradient(135deg, #f5f0ff 0%, #ffffff 100%)',
      }}>
        <div style={{ fontSize: '48px' }}>📱</div>
        <h2 style={{ color: '#7210FF', margin: 0 }}>{t.openingApp}</h2>
        <p style={{ color: '#6b7280' }}>{t.pleaseWait}</p>
      </div>
    );
  }

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : '';
  const isMobile = /android|iphone|ipad|ipod/.test(ua);
  const qrPx = isMobile ? 132 : 168;
  const qrCodeUrl = getQrCodeUrl(playStoreUrlWithParams, qrPx);

  const headerRadialBg = [
    'radial-gradient(ellipse 100% 180% at 0% 0%, rgba(255,255,255,0.2) 0%, transparent 52%)',
    'radial-gradient(ellipse 90% 120% at 100% 100%, rgba(255, 211, 0, 0.14) 0%, transparent 48%)',
    'radial-gradient(circle at 70% -30%, rgba(151, 71, 255, 0.95) 0%, transparent 45%)',
    'radial-gradient(ellipse 80% 100% at 20% 110%, rgba(88, 12, 200, 0.55) 0%, transparent 42%)',
    'linear-gradient(155deg, #4f0bb8 0%, #7210FF 42%, #8f52ff 78%, #6510d4 100%)',
  ].join(', ');

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
      background: 'linear-gradient(135deg, #f5f0ff 0%, #ffffff 100%)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '12px',
    }}>
      <div style={{
        maxWidth: '480px', width: '100%', background: '#fff',
        borderRadius: '20px', boxShadow: '0 4px 24px rgba(114, 16, 255, 0.1)', overflow: 'hidden',
      }}>
        {/* Header — compact, logo left, radial brand backdrop */}
        <div style={{
          background: headerRadialBg,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          textAlign: 'left',
        }}>
          <img
            src={neproLogo}
            alt="NePro"
            style={{ height: 36, width: 'auto', flexShrink: 0, display: 'block', objectFit: 'contain' }}
          />
          <p style={{
            color: 'rgba(255,255,255,0.95)',
            margin: 0,
            fontSize: '18px',
            lineHeight: 1.35,
            fontWeight: 500,
            flex: 1,
            minWidth: 0,
          }}>
            {translating ? '...' : t.tagline}
          </p>
        </div>

        {/* Content — mobile-first spacing; col1: benefits + CTA, col2: QR */}
        <div
          style={{
            padding: '14px 14px 16px',
            textAlign: 'center',
            backgroundColor: 'rgba(246, 240, 255, 1)',
            color: 'rgba(0, 0, 0, 0)',
            backgroundClip: 'unset',
            WebkitBackgroundClip: 'unset',
          }}
        >
          <h2 style={{ color: '#1a1a2e', fontSize: 'clamp(17px, 4.2vw, 20px)', margin: '0 0 6px', lineHeight: 1.25 }}>
            {t.headline}
          </h2>
          <p style={{ color: '#6b7280', fontSize: '13px', lineHeight: 1.5, margin: '0 0 12px' }}>
            {t.description}
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gap: '10px',
              alignItems: 'start',
              marginBottom: '10px',
            }}
          >
            {/* Column 1: benefits + Play Store CTA */}
            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'left' }}>
              <div style={{
                background: '#f8f4ff',
                borderRadius: '10px',
                padding: '10px 10px',
                border: '1px solid rgba(114, 16, 255, 0.08)',
              }}>
                <div style={{ fontSize: '12px', color: '#374151', lineHeight: 1.45 }}>
                  {t.benefits.map((benefit, i) => (
                    <div key={i} style={{ marginBottom: i < t.benefits.length - 1 ? 6 : 0 }}>{benefit}</div>
                  ))}
                </div>
              </div>
              <a
                href={playStoreUrlWithParams}
                onClick={() => MetaPixel.playStoreClick({ source: ref, leadId: id })}
                style={{
                  display: 'block',
                  background: '#7210FF',
                  color: '#ffffff',
                  textDecoration: 'none',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  width: '100%',
                  boxSizing: 'border-box',
                  lineHeight: 1.25,
                  textAlign: 'center',
                }}
              >
                {t.downloadCta}
              </a>
            </div>

            {/* Column 2: QR + helper copy */}
            <div style={{ textAlign: 'center', minWidth: 0 }}>
              <p style={{ color: '#374151', fontWeight: 600, fontSize: '11px', margin: '0 0 6px', lineHeight: 1.25 }}>
                {t.scanQr}
              </p>
              <div style={{
                display: 'inline-block',
                padding: '6px',
                background: '#fff',
                borderRadius: '10px',
                border: '1px solid #e9d5ff',
                marginBottom: '8px',
                lineHeight: 0,
              }}>
                <img
                  src={qrCodeUrl}
                  alt="QR Code to download NePro"
                  width={qrPx}
                  height={qrPx}
                  style={{ display: 'block', borderRadius: '6px', width: '100%', maxWidth: qrPx, height: 'auto' }}
                />
              </div>
              <p style={{ color: '#6b7280', fontSize: '10px', margin: '0 0 8px', lineHeight: 1.3 }}>
                {t.orScanQr}
              </p>
              {!isMobile && (
                <p style={{ color: '#9ca3af', fontSize: '10px', margin: 0, lineHeight: 1.35 }}>
                  {t.desktopNote}
                </p>
              )}
            </div>
          </div>

          <p style={{ color: '#9ca3af', fontSize: '11px', margin: 0 }}>
            {t.availableOn}
          </p>
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 14px', background: '#f9fafb', textAlign: 'center' }}>
          <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>
            <a href="https://nepro.in" style={{ color: '#7210FF' }}>nepro.in</a> •{' '}
            <a href="https://www.instagram.com/neprotech" style={{ color: '#7210FF' }}>Instagram</a> •{' '}
            <a href="https://www.facebook.com/neprotech" style={{ color: '#7210FF' }}>Facebook</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default JoinRedirect;
